/**
 * Hook for managing intentions selection
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { createCustomIntention, getIntentions, toggleIntention } from "../services/user.service"

export function useIntentions(userId?: string) {
  const queryClient = useQueryClient()

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["intentions", userId ?? "public"],
    queryFn: () =>
      getIntentions(userId ? { page: 1, limit: 100, user_id: userId } : { page: 1, limit: 100 }),
    staleTime: 5 * 60 * 1000,
  })

  const toggleMutation = useMutation({
    mutationFn: (intentionId: string) => toggleIntention(intentionId),
    onSuccess: () => {
      // Refetch intentions to get updated state
      queryClient.invalidateQueries({ queryKey: ["intentions", userId] })
    },
  })

  const createCustomMutation = useMutation({
    mutationFn: (data: { name: string; description?: string }) => createCustomIntention(data),
    onSuccess: () => {
      // Refetch intentions to get updated state
      queryClient.invalidateQueries({ queryKey: ["intentions", userId] })
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create custom intention")
    },
  })

  return {
    intentions: data?.intentions ?? [],
    isLoading,
    error,
    refetch,
    toggleIntention: toggleMutation.mutate,
    isToggling: toggleMutation.isPending,
    createCustomIntention: createCustomMutation.mutate,
    isCreating: createCustomMutation.isPending,
  }
}
